import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useCopyToClipboard } from 'usehooks-ts';
import { cn } from '../../utils/className';
import { CopyButtonProps } from './types';

const COPIED_FEEDBACK_MS = 2000;

export const CopyButton = ({
  value,
  title,
  iconSize = 'size-3',
  iconClassName = 'text-text-general-neutral-mid',
  className: propsClassName,
  onCopy,
}: CopyButtonProps) => {
  const [, copy] = useCopyToClipboard();
  const [hasJustCopied, setHasJustCopied] = useState(false);

  useEffect(() => {
    if (!hasJustCopied) return undefined;

    const timeoutId = window.setTimeout(
      () => setHasJustCopied(false),
      COPIED_FEEDBACK_MS,
    );

    return () => window.clearTimeout(timeoutId);
  }, [hasJustCopied]);

  const handleCopy = async () => {
    const copied = await copy(value);

    if (copied) {
      setHasJustCopied(true);
      onCopy?.();
    }
  };

  const className = cn(
    'p-1 rounded transition-colors',
    hasJustCopied ? 'bg-fill-tag-positive-idle' : 'hover:bg-purple-muted-600',
    propsClassName,
  );

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void handleCopy();
      }}
      className={className}
      title={title}
      aria-label={title}
    >
      {hasJustCopied ? (
        <Check className={cn(iconSize, 'text-text-tag-positive-idle')} />
      ) : (
        <Copy className={cn(iconSize, iconClassName)} />
      )}
    </button>
  );
};
